// MonitorClases — Supervisión de Clases en Vivo (solo Super Usuarios / Admins).
// Lista todas las clases programadas y activas de todos los docentes y permite
// "Unirse como Supervisor" (entra a la sala silenciado, sin ser asignado).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Video, Users, UserCheck, Clock, RefreshCw, Loader2, Eye } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function MonitorCard({ cls, onSupervise }) {
  if (!cls || !cls.id) return null;
  const live = cls.is_live_now;
  return (
    <div
      className={`jasq-card p-4 flex flex-col gap-3 border-2 ${live ? "border-[#A52258]" : "border-[#E1E5F0]"}`}
      data-testid={`monitor-card-${cls.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {live ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-white bg-[#A52258] rounded-full px-2 py-0.5" data-testid="monitor-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> EN VIVO
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-[#2C49A0] bg-[#E8EEFB] rounded-full px-2 py-0.5">
                <Clock size={10} /> Programada
              </span>
            )}
          </div>
          <h3 className="font-display text-base font-extrabold text-[#1F2547] truncate">{cls.title || "Clase"}</h3>
          <p className="text-xs text-[#6E7591] truncate">Docente: <span className="font-bold text-[#4C5478]">{cls.teacher_name}</span></p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#6E7591]">
        <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmtTime(cls.scheduled_at)} · {cls.duration_min || 60} min</span>
        <span className="inline-flex items-center gap-1"><Users size={12} /> {cls.student_count} inscritos</span>
        {live && <span className="inline-flex items-center gap-1 text-[#3FA84D] font-bold"><UserCheck size={12} /> {cls.joined_count} conectados</span>}
      </div>
      <button
        type="button"
        onClick={() => onSupervise(cls)}
        disabled={!cls.has_room}
        data-testid={`monitor-supervise-${cls.id}`}
        className="mt-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-[#1F2547] text-white text-xs font-extrabold hover:bg-[#2C49A0] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        title={cls.has_room ? "Entra a la sala como supervisor (micrófono silenciado)" : "Esta clase aún no tiene sala"}
      >
        <Eye size={14} /> Unirse como Supervisor
      </button>
    </div>
  );
}

export default function MonitorClases() {
  const navigate = useNavigate();
  const [data, setData] = useState({ live: [], upcoming: [] });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/live-classes/monitor/live");
      setData({ live: Array.isArray(data?.live) ? data.live : [], upcoming: Array.isArray(data?.upcoming) ? data.upcoming : [] });
    } catch (e) {
      if (!silent) toast.error("No se pudo cargar el monitor de clases");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const iv = setInterval(() => refresh(true), 15000); // auto-refresh en vivo
    return () => clearInterval(iv);
  }, [refresh]);

  const onSupervise = (cls) => {
    navigate(`/admin/clases-en-vivo/${cls.id}/sala?supervisor=1`);
  };

  const total = (data.live?.length || 0) + (data.upcoming?.length || 0);

  return (
    <div className="space-y-6" data-testid="monitor-clases-page">
      <header className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790]">Supervisión</p>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-[#1F2547] inline-flex items-center gap-2">
            <Radio size={22} className="text-[#A52258]" /> Monitor de Clases en Vivo
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">Todas las clases activas y programadas de todos los docentes. Ingresa como supervisor sin interrumpir.</p>
        </div>
        <button onClick={() => refresh()} data-testid="monitor-refresh" className="jasq-btn-ghost inline-flex items-center gap-2 text-xs shrink-0">
          <RefreshCw size={14} /> Actualizar
        </button>
      </header>

      {loading ? (
        <div className="jasq-card p-10 text-center"><Loader2 size={28} className="mx-auto animate-spin text-[#A43790]" /></div>
      ) : total === 0 ? (
        <div className="jasq-card p-10 text-center space-y-2" data-testid="monitor-empty">
          <Video size={30} className="mx-auto text-[#C7CBD9]" />
          <p className="text-sm font-bold text-[#4C5478]">No hay clases activas ni programadas por ahora.</p>
        </div>
      ) : (
        <>
          <section>
            <h2 className="text-sm font-extrabold text-[#A52258] mb-2 inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#A52258] animate-pulse" /> En vivo ahora ({data.live.length})
            </h2>
            {data.live.length === 0 ? (
              <p className="text-xs text-[#6E7591] mb-4">Ninguna clase en vivo en este momento.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6" data-testid="monitor-live-grid">
                {data.live.filter((c) => c && c.id).map((cls) => (
                  <MonitorCard key={cls.id} cls={cls} onSupervise={onSupervise} />
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-sm font-extrabold text-[#2C49A0] mb-2 inline-flex items-center gap-2">
              <Clock size={14} /> Programadas ({data.upcoming.length})
            </h2>
            {data.upcoming.length === 0 ? (
              <p className="text-xs text-[#6E7591]">No hay próximas clases programadas.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="monitor-upcoming-grid">
                {data.upcoming.filter((c) => c && c.id).map((cls) => (
                  <MonitorCard key={cls.id} cls={cls} onSupervise={onSupervise} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
